/**
 * Discovery settings (contract §1): the numbers and word lists the discovery
 * server used to hard-code — folder archiving, search defaults, Max reach
 * shards, classifier keywords, status sync, near-miss caps.
 *
 * Any admin may edit. Saving shows exactly what changes before it's sent,
 * and every change lands in the audit list at the bottom (who, when,
 * old → new). Each field shows its default and can be reset to it.
 *
 * Mounted by the folder workspace at /admin/discovery/settings.
 */
import { useEffect, useMemo, useState, type JSX, type KeyboardEvent } from 'react';
import { History, Loader2, RotateCcw, Settings2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { cn } from '@/lib/utils';
import { FieldApiError } from '../api/client';
import type { SettingField, SettingsAuditEntry, SettingValue } from '../api/types';
import { FieldError, relativeTime } from '../search/controls';
import { useDiscoverySettings, useResetSettings, useSaveSettings, useSettingsAudit } from '../search/hooks';
import {
  addTags,
  changesToValues,
  formatSettingValue,
  groupSettingFields,
  settingEquals,
  settingsDiff,
  validateSettings,
  type SettingChange,
} from '../search/settingsModel';

export default function SettingsScreen(): JSX.Element {
  const settings = useDiscoverySettings();
  const audit = useSettingsAudit(50);
  const save = useSaveSettings();
  const reset = useResetSettings();
  const [draft, setDraft] = useState<Record<string, SettingValue>>({});
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [resetting, setResetting] = useState<SettingField | null>(null);

  const data = settings.data;
  // Start the draft from the saved values the first time they arrive.
  useEffect(() => {
    if (data && Object.keys(draft).length === 0) setDraft({ ...data.values });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const fields = useMemo(() => data?.fields ?? [], [data]);
  const groups = useMemo(() => groupSettingFields(fields), [fields]);
  const changes = useMemo(() => (data ? settingsDiff(fields, data.values, draft) : []), [fields, data, draft]);
  const clientErrors = useMemo(() => validateSettings(fields.filter((f) => changes.some((c) => c.key === f.key)), draft), [fields, changes, draft]);
  const errors = { ...serverErrors, ...clientErrors };
  const labelOf = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields]);

  function setValue(key: string, v: SettingValue) {
    setDraft((d) => ({ ...d, [key]: v }));
    if (serverErrors[key]) {
      setServerErrors((e) => {
        const { [key]: _gone, ...rest } = e;
        return rest;
      });
    }
  }

  function confirmSave() {
    save.mutate(changesToValues(changes), {
      onSuccess: (res) => {
        setConfirming(false);
        setServerErrors({});
        if (res?.values) setDraft({ ...res.values });
        toast.success(`Saved ${changes.length} setting${changes.length === 1 ? '' : 's'}`);
      },
      onError: (err) => {
        setConfirming(false);
        if (err instanceof FieldApiError && err.fieldErrors) setServerErrors(err.fieldErrors);
        else toast.error((err as Error).message || 'Could not save settings');
      },
    });
  }

  if (settings.isLoading) {
    return (
      <div className="space-y-5">
        <Header />
        <p className="text-sm text-muted-foreground">Loading settings…</p>
      </div>
    );
  }

  if (settings.error || !data) {
    return (
      <div className="space-y-5">
        <Header />
        <div className="panel rounded-xl">
          <EmptyState
            icon={<Settings2 className="h-6 w-6" />}
            title="Settings aren't available yet"
            hint={
              settings.error instanceof Error
                ? `${settings.error.message} The discovery server needs the settings update before these can be edited here.`
                : 'The discovery server needs the settings update before these can be edited here.'
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      <Header
        meta={
          data.updatedAt
            ? `Last changed ${relativeTime(data.updatedAt)}${data.updatedByName ? ` by ${data.updatedByName}` : ''}`
            : null
        }
      />

      {groups.map((g) => (
        <section key={g.group} className="panel rounded-xl" aria-labelledby={`settings-${g.group}`}>
          <h2 id={`settings-${g.group}`} className="border-b border-border px-5 py-3.5 text-[15px] font-bold">
            {g.group}
          </h2>
          <ul className="divide-y divide-border">
            {g.fields.map((f) => (
              <SettingRow
                key={f.key}
                field={f}
                saved={data.values[f.key] ?? null}
                value={draft[f.key] ?? null}
                defaultValue={data.defaults[f.key] ?? null}
                error={errors[f.key]}
                onChange={(v) => setValue(f.key, v)}
                onUndo={() => setValue(f.key, data.values[f.key] ?? null)}
                onReset={() => setResetting(f)}
              />
            ))}
          </ul>
        </section>
      ))}

      <AuditList rows={audit.data ?? []} loading={audit.isLoading} failed={!!audit.error} fieldOf={(k) => labelOf.get(k)} />

      {changes.length > 0 && (
        <div className="panel sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl px-4 py-3">
          <p className="text-sm">
            <span className="font-semibold">
              {changes.length} unsaved change{changes.length === 1 ? '' : 's'}
            </span>
            {Object.keys(clientErrors).length > 0 && <span className="text-destructive-strong"> · fix the highlighted fields first</span>}
          </p>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={() => { setDraft({ ...data.values }); setServerErrors({}); }}>
              Discard
            </Button>
            <Button type="button" disabled={Object.keys(clientErrors).length > 0} onClick={() => setConfirming(true)}>
              Review and save
            </Button>
          </div>
        </div>
      )}

      <SaveDialog
        open={confirming}
        onOpenChange={setConfirming}
        changes={changes}
        fieldOf={(k) => labelOf.get(k)}
        pending={save.isPending}
        onConfirm={confirmSave}
      />

      <ConfirmDialog
        open={resetting !== null}
        onOpenChange={(v) => !v && setResetting(null)}
        title={resetting ? `Reset “${resetting.label}” to its default?` : 'Reset to default?'}
        description={
          resetting
            ? `${formatSettingValue(resetting, data.values[resetting.key])} → ${formatSettingValue(resetting, data.defaults[resetting.key])}. The change is recorded in the audit list.`
            : undefined
        }
        confirmLabel="Reset to default"
        pending={reset.isPending}
        onConfirm={() => {
          const f = resetting;
          if (!f) return;
          reset.mutate([f.key], {
            onSuccess: (res) => {
              setDraft((d) => ({ ...d, [f.key]: res?.values?.[f.key] ?? data.defaults[f.key] ?? null }));
              toast.success(`${f.label} reset to default`);
            },
            onError: (err) => toast.error((err as Error).message || 'Could not reset'),
          });
        }}
      />
    </div>
  );
}

function Header({ meta }: { meta?: string | null }): JSX.Element {
  return (
    <PageHeader
      title="Discovery settings"
      description={
        <>
          Defaults and limits the discovery server runs on. Any admin can change them; every change is recorded
          below.{meta ? ` ${meta}.` : ''}
        </>
      }
    />
  );
}

function SettingRow({
  field,
  saved,
  value,
  defaultValue,
  error,
  onChange,
  onUndo,
  onReset,
}: {
  field: SettingField;
  saved: SettingValue;
  value: SettingValue;
  defaultValue: SettingValue;
  error?: string;
  onChange: (v: SettingValue) => void;
  onUndo: () => void;
  onReset: () => void;
}): JSX.Element {
  const id = `setting-${field.key}`;
  const dirty = !settingEquals(saved, value);
  const savedIsDefault = settingEquals(saved, defaultValue);
  return (
    <li className={cn('grid gap-3 px-5 py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] md:gap-6', dirty && 'bg-brand/[0.04]')}>
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-semibold">
          {field.label}
        </label>
        {field.help && <p className="mt-0.5 text-sm text-muted-foreground">{field.help}</p>}
        <p className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <span>
            Default: <span className="font-medium text-foreground/80">{formatSettingValue(field, defaultValue)}</span>
          </span>
          {dirty && (
            <span>
              · Saved: <span className="font-medium text-foreground/80">{formatSettingValue(field, saved)}</span>
            </span>
          )}
        </p>
      </div>
      <div className="grid content-start gap-1.5">
        <SettingEditor id={id} field={field} value={value} onChange={onChange} invalid={!!error} />
        <FieldError message={error} />
        <div className="flex flex-wrap items-center gap-x-3">
          {dirty && (
            <Button type="button" variant="link" size="sm" className="h-auto px-0" onClick={onUndo}>
              Undo change
            </Button>
          )}
          {!savedIsDefault && (
            <Button type="button" variant="link" size="sm" className="h-auto px-0 text-muted-foreground" onClick={onReset}>
              <RotateCcw className="h-3.5 w-3.5" />
              Reset to default
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

function SettingEditor({
  id,
  field,
  value,
  onChange,
  invalid,
}: {
  id: string;
  field: SettingField;
  value: SettingValue;
  onChange: (v: SettingValue) => void;
  invalid: boolean;
}): JSX.Element {
  switch (field.type) {
    case 'boolean':
      return (
        <label htmlFor={id} className="inline-flex cursor-pointer items-center gap-2.5 text-sm">
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            onChange={(ev) => onChange(ev.target.checked)}
            className="h-4 w-4 rounded border-input accent-[var(--brand)]"
          />
          {value === true ? 'On' : 'Off'}
        </label>
      );
    case 'integer':
    case 'number':
    case 'percent': {
      const pctFraction = field.type === 'percent' && field.max != null && field.max <= 1;
      const shown = typeof value === 'number' ? (pctFraction ? Math.round(value * 1000) / 10 : value) : '';
      return (
        <div className="relative max-w-[12rem]">
          <Input
            id={id}
            type="number"
            inputMode="decimal"
            aria-invalid={invalid || undefined}
            min={field.min != null ? (pctFraction ? field.min * 100 : field.min) : undefined}
            max={field.max != null ? (pctFraction ? field.max * 100 : field.max) : undefined}
            step={field.type === 'number' ? 'any' : 1}
            value={shown}
            onChange={(ev) => {
              const raw = ev.target.value;
              if (raw.trim() === '') return onChange(null);
              const n = Number(raw);
              onChange(Number.isFinite(n) ? (pctFraction ? n / 100 : n) : null);
            }}
            className={cn(field.type === 'percent' && 'pr-7', invalid && 'border-destructive/70')}
          />
          {field.type === 'percent' && (
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
          )}
        </div>
      );
    }
    case 'stringList':
      return <TagEditor id={id} value={Array.isArray(value) ? value : []} onChange={onChange} invalid={invalid} />;
    default:
      return (
        <Input
          id={id}
          aria-invalid={invalid || undefined}
          value={typeof value === 'string' ? value : value == null ? '' : String(value)}
          onChange={(ev) => onChange(ev.target.value)}
          className={cn(invalid && 'border-destructive/70')}
        />
      );
  }
}

/** Word lists as removable tags; Enter or a comma adds, Backspace on empty removes the last. */
function TagEditor({
  id,
  value,
  onChange,
  invalid,
}: {
  id: string;
  value: string[];
  onChange: (v: string[]) => void;
  invalid: boolean;
}): JSX.Element {
  const [text, setText] = useState('');
  function commit() {
    if (!text.trim()) return;
    onChange(addTags(value, text));
    setText('');
  }
  function onKeyDown(ev: KeyboardEvent<HTMLInputElement>) {
    if (ev.key === 'Enter' || ev.key === ',') {
      ev.preventDefault();
      commit();
    } else if (ev.key === 'Backspace' && !text && value.length) {
      onChange(value.slice(0, -1));
    }
  }
  return (
    <div
      className={cn(
        'flex min-h-9 flex-wrap items-center gap-1 rounded-lg border bg-[var(--input-bg)] px-1.5 py-1 shadow-control',
        'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/25',
        invalid ? 'border-destructive/70' : 'border-input',
      )}
    >
      {value.map((tag) => (
        <span key={tag} className="inline-flex items-center gap-1 rounded-md bg-muted py-0.5 pl-2 pr-1 text-sm">
          {tag}
          <button
            type="button"
            onClick={() => onChange(value.filter((t) => t !== tag))}
            aria-label={`Remove ${tag}`}
            title={`Remove ${tag}`}
            className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ))}
      <input
        id={id}
        value={text}
        onChange={(ev) => setText(ev.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={value.length ? 'Add…' : 'Type and press Enter'}
        className="h-7 min-w-[8rem] flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-muted-foreground/70 pointer-coarse:text-base"
      />
    </div>
  );
}

function SaveDialog({
  open,
  onOpenChange,
  changes,
  fieldOf,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  changes: SettingChange[];
  fieldOf: (key: string) => SettingField | undefined;
  pending: boolean;
  onConfirm: () => void;
}): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Save {changes.length} change{changes.length === 1 ? '' : 's'}?
          </DialogTitle>
          <DialogDescription>Takes effect for the whole team on the next search or sweep, and is recorded in the audit list.</DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-border rounded-lg border border-border">
          {changes.map((c) => {
            const f = fieldOf(c.key);
            return (
              <li key={c.key} className="px-3 py-2 text-sm">
                <p className="font-medium">{c.label}</p>
                <p className="mt-0.5 break-words text-muted-foreground">
                  <span className="line-through decoration-muted-foreground/60">{formatSettingValue(f, c.from)}</span>
                  {' → '}
                  <span className="font-medium text-foreground">{formatSettingValue(f, c.to)}</span>
                </p>
              </li>
            );
          })}
        </ul>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={pending} onClick={onConfirm}>
            {pending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditList({
  rows,
  loading,
  failed,
  fieldOf,
}: {
  rows: SettingsAuditEntry[];
  loading: boolean;
  failed: boolean;
  fieldOf: (key: string) => SettingField | undefined;
}): JSX.Element {
  return (
    <section className="panel rounded-xl" aria-labelledby="settings-audit">
      <div className="flex items-center gap-2 border-b border-border px-5 py-3.5">
        <History className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
        <h2 id="settings-audit" className="text-[15px] font-bold">
          Recent changes
        </h2>
      </div>
      {loading ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">Loading…</p>
      ) : failed ? (
        <p className="px-5 py-4 text-sm text-muted-foreground">The change history isn't available on this server yet.</p>
      ) : rows.length === 0 ? (
        <EmptyState title="No changes yet" hint="Every saved change shows here with who made it and the old and new values." className="py-8" />
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((r, i) => {
            const f = fieldOf(r.key);
            return (
              <li key={`${r.key}-${r.changedAt}-${i}`} className="flex flex-wrap items-baseline gap-x-2 px-5 py-2.5 text-sm">
                <span className="font-medium">{r.changedByName ?? 'Someone'}</span>
                <span className="text-muted-foreground">changed</span>
                <span className="font-medium">{f?.label ?? r.key}</span>
                <span className="min-w-0 break-words text-muted-foreground">
                  {formatSettingValue(f, r.oldValue)} → <span className="text-foreground">{formatSettingValue(f, r.newValue)}</span>
                </span>
                <time dateTime={r.changedAt} title={new Date(r.changedAt).toLocaleString()} className="ml-auto text-xs text-muted-foreground">
                  {relativeTime(r.changedAt)}
                </time>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
